import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Calendar, MapPin, Clock, Users, ExternalLink, Video, GraduationCap, Mic2, ArrowRight, ChevronRight, Image as ImageIcon } from 'lucide-react';
import SiteHeader from '@/components/SiteHeader';
import Footer from '@/components/Footer';
import SEO from '@/components/SEO';
import { API } from '@/App';

const typeConfig = {
  conference: { label: 'Congrès', icon: Mic2, color: '#35AFE4', bg: '#eaf6fc', gradient: 'from-[#35AFE4] to-[#1e3a5f]' },
  webinar: { label: 'Webinaire', icon: Video, color: '#7c3aed', bg: '#f3e8ff', gradient: 'from-[#7c3aed] to-[#4c1d95]' },
  training: { label: 'Formation', icon: GraduationCap, color: '#059669', bg: '#d1fae5', gradient: 'from-[#059669] to-[#065f46]' },
  formation: { label: 'Formation', icon: GraduationCap, color: '#059669', bg: '#d1fae5', gradient: 'from-[#059669] to-[#065f46]' },
  workshop: { label: 'Atelier', icon: Users, color: '#d97706', bg: '#fef3c7', gradient: 'from-[#d97706] to-[#92400e]' },
};

const parseDate = (dateStr) => {
  if (!dateStr) return null;
  return new Date(dateStr);
};

const formatDateFr = (dateStr) => {
  if (!dateStr) return '';
  const d = parseDate(dateStr);
  if (!d || isNaN(d.getTime())) return '';
  return d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });
};

const formatDateShort = (dateStr) => {
  if (!dateStr) return { day: '', month: '' };
  const d = parseDate(dateStr);
  if (!d || isNaN(d.getTime())) return { day: '', month: '' };
  return { day: d.getDate(), month: d.toLocaleDateString('fr-FR', { month: 'short' }).toUpperCase().replace('.', '') };
};

const stripHtml = (html) => {
  if (!html) return '';
  return html.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').replace(/&amp;/g, '&').replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&quot;/g, '"');
};

const isUpcoming = (dateStr) => {
  if (!dateStr) return false;
  return new Date(dateStr) >= new Date(new Date().toDateString());
};

const isPast = (dateStr) => !isUpcoming(dateStr);

const AnimatedCard = ({ children, delay }) => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setVisible(true); }, { threshold: 0.1 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);
  return (
    <div ref={ref} className="transition-all duration-700 ease-out" style={{ opacity: visible ? 1 : 0, transform: visible ? 'translateY(0)' : 'translateY(24px)', transitionDelay: `${delay}ms` }}>
      {children}
    </div>
  );
};

const EventCard = ({ event, featured = false, onNavigate }) => {
  const cfg = typeConfig[event.event_type] || typeConfig.conference;
  const Icon = cfg.icon;
  const dateShort = formatDateShort(event.start_date);
  const upcoming = isUpcoming(event.end_date || event.start_date);
  const isSameDay = event.start_date && event.end_date && 
    new Date(event.start_date).toDateString() === new Date(event.end_date).toDateString();
  const dateRange = isSameDay || !event.end_date
    ? formatDateFr(event.start_date)
    : `${formatDateFr(event.start_date)} — ${formatDateFr(event.end_date)}`;
  const hasGallery = (event.gallery_images || []).length > 0;
  const hasRecap = !!event.recap_video_url;

  if (featured) {
    return (
      <div className="relative rounded-2xl overflow-hidden group cursor-pointer" onClick={() => onNavigate(event.id)} data-testid="featured-event">
        {/* Background image or gradient */}
        {event.poster_image ? (
          <>
            <img src={event.poster_image} alt="" className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
            <div className="absolute inset-0" style={{ background: 'linear-gradient(135deg, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0.4) 100%)' }} />
          </>
        ) : (
          <div className={`absolute inset-0 bg-gradient-to-br ${cfg.gradient}`} />
        )}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute -top-20 -right-20 w-80 h-80 rounded-full bg-white/20" />
        </div>
        <div className="relative z-10 p-8 sm:p-10 flex flex-col sm:flex-row gap-8 items-start">
          <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-2xl bg-white/15 backdrop-blur-sm flex flex-col items-center justify-center text-white flex-shrink-0" style={{ border: '1px solid rgba(255,255,255,0.2)' }}>
            <span className="text-3xl sm:text-4xl font-bold leading-none" style={{ fontFamily: "'Barlow', sans-serif" }}>{dateShort.day}</span>
            <span className="text-xs font-semibold tracking-wider mt-0.5">{dateShort.month}</span>
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-3 flex-wrap">
              <span className="inline-flex items-center gap-1.5 px-3 py-1 text-xs font-bold rounded-full text-white" style={{ background: cfg.color }}>
                <Icon size={12} /> {cfg.label}
              </span>
              {upcoming && event.registration_open && (
                <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-green-400/30 text-green-100 text-xs font-semibold rounded-full">
                  <span className="w-1.5 h-1.5 bg-green-300 rounded-full animate-pulse" /> Inscriptions ouvertes
                </span>
              )}
              {!upcoming && hasRecap && (
                <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-white/20 text-white text-xs font-semibold rounded-full">
                  <ImageIcon size={10} /> Retour en images
                </span>
              )}
            </div>
            <h2 className="text-xl sm:text-2xl font-bold text-white mb-3 leading-tight group-hover:underline underline-offset-4 decoration-2" style={{ fontFamily: "'Barlow', sans-serif" }} data-testid="featured-event-title">
              {event.title}
            </h2>
            <p className="text-white/80 text-sm leading-relaxed mb-5 line-clamp-3">{stripHtml(event.description)}</p>
            <div className="flex flex-wrap gap-4 text-white/70 text-sm mb-5">
              <span className="flex items-center gap-1.5"><Calendar size={14} /> {dateRange}</span>
              {event.location && <span className="flex items-center gap-1.5"><MapPin size={14} /> {event.location}</span>}
              {event.location_type === 'online' && <span className="flex items-center gap-1.5"><Video size={14} /> En ligne</span>}
              {event.max_participants && <span className="flex items-center gap-1.5"><Users size={14} /> {event.max_participants} places</span>}
            </div>
            <span className="inline-flex items-center gap-2 px-6 py-2.5 bg-white text-gray-900 rounded-full text-sm font-bold group-hover:shadow-lg transition-all" data-testid="featured-see-more-btn">
              {upcoming ? "S'inscrire" : 'Voir le récapitulatif'} <ArrowRight size={14} />
            </span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`bg-white rounded-xl border-2 overflow-hidden transition-all hover:shadow-lg group cursor-pointer ${upcoming ? 'border-gray-100 hover:border-[' + cfg.color + ']' : 'border-gray-100'}`} onClick={() => onNavigate(event.id)} data-testid={`event-card-${event.id}`}>
      <div className="flex">
        {/* Left: poster or date */}
        {event.poster_image ? (
          <div className="w-28 sm:w-36 flex-shrink-0 relative overflow-hidden">
            <img src={event.poster_image} alt="" className="absolute inset-0 w-full h-full object-cover transition-transform duration-300 group-hover:scale-110" />
            <div className="absolute inset-0 bg-black/30 flex flex-col items-center justify-center text-white">
              <span className="text-2xl font-bold leading-none" style={{ fontFamily: "'Barlow', sans-serif" }}>{dateShort.day}</span>
              <span className="text-[10px] font-bold tracking-wider mt-0.5">{dateShort.month}</span>
            </div>
            {!upcoming && <span className="absolute bottom-1 left-1/2 -translate-x-1/2 text-[8px] font-bold text-white/70 bg-black/40 px-1.5 py-0.5 rounded-full">PASSÉ</span>}
          </div>
        ) : (
          <div className="w-20 sm:w-24 flex-shrink-0 flex flex-col items-center justify-center py-6" style={{ background: cfg.bg }}>
            <span className="text-2xl sm:text-3xl font-bold leading-none" style={{ color: cfg.color, fontFamily: "'Barlow', sans-serif" }}>{dateShort.day}</span>
            <span className="text-[10px] font-bold tracking-wider mt-1" style={{ color: cfg.color }}>{dateShort.month}</span>
            {!upcoming && <span className="text-[9px] text-gray-400 mt-2 font-semibold">PASSÉ</span>}
          </div>
        )}
        {/* Content */}
        <div className="flex-1 p-5 min-w-0">
          <div className="flex items-center gap-2 mb-2 flex-wrap">
            <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-bold rounded-full" style={{ background: cfg.bg, color: cfg.color }}>
              <Icon size={10} /> {cfg.label}
            </span>
            {event.location_type === 'online' && <span className="text-[10px] text-purple-500 font-semibold">EN LIGNE</span>}
            {!upcoming && hasGallery && <span className="text-[10px] text-gray-400 flex items-center gap-0.5"><ImageIcon size={10} /> Photos</span>}
            {!upcoming && hasRecap && <span className="text-[10px] text-gray-400 flex items-center gap-0.5"><Video size={10} /> Replay</span>}
          </div>
          <h3 className="text-base font-bold text-gray-900 mb-2 group-hover:text-[#35AFE4] transition-colors leading-tight" style={{ fontFamily: "'Barlow', sans-serif" }}>
            {event.title}
          </h3>
          <p className="text-gray-500 text-xs line-clamp-2 mb-3">{stripHtml(event.description)}</p>
          <div className="flex flex-wrap items-center gap-3 text-xs text-gray-400">
            <span className="flex items-center gap-1"><Calendar size={12} /> {dateRange}</span>
            {event.location && <span className="flex items-center gap-1"><MapPin size={12} /> {event.location}</span>}
            {event.max_participants && <span className="flex items-center gap-1"><Users size={12} /> {event.max_participants} places</span>}
          </div>
          <div className="mt-3 pt-3 border-t border-gray-50">
            {upcoming && event.registration_open ? (
              <span className="inline-flex items-center gap-1.5 text-xs font-semibold rounded-full px-4 py-1.5 transition-all" style={{ background: cfg.bg, color: cfg.color }}>
                S'inscrire <ChevronRight size={12} />
              </span>
            ) : (
              <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-gray-400">
                {!upcoming ? 'Voir le récapitulatif' : 'Voir les détails'} <ChevronRight size={12} />
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const PublicEventsPage = () => {
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [viewMode, setViewMode] = useState('upcoming');

  useEffect(() => {
    axios.get(`${API}/events?published_only=true`)
      .then(r => {
        const sorted = r.data.sort((a, b) => new Date(b.start_date) - new Date(a.start_date));
        setEvents(sorted);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const goToEvent = (id) => navigate(`/events/${id}`);

  const upcoming = events.filter(e => isUpcoming(e.end_date || e.start_date));
  const past = events.filter(e => isPast(e.end_date || e.start_date));
  const featured = upcoming[0];
  const otherUpcoming = upcoming.slice(1);

  const typeCounts = {};
  const activeList = viewMode === 'upcoming' ? upcoming : past;
  events.forEach(e => { typeCounts[e.event_type] = (typeCounts[e.event_type] || 0) + 1; });
  const filteredList = filter === 'all' ? activeList : activeList.filter(e => e.event_type === filter);

  return (
    <div className="min-h-screen bg-gray-50" style={{ fontFamily: "'Roboto', sans-serif" }}>
      <SiteHeader />
      <SEO title="Événements" description="Agenda des événements SFOALC. Congrès, formations, webinaires et reunions regionales en ophtalmologie et contactologie." />

      {/* Hero */}
      <section className="relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #1e3a5f 0%, #35AFE4 100%)' }}>
        <div className="absolute inset-0 opacity-5">
          <div className="absolute top-0 right-0 w-96 h-96 rounded-full bg-white" style={{ transform: 'translate(30%, -30%)' }} />
          <div className="absolute bottom-0 left-0 w-64 h-64 rounded-full bg-white" style={{ transform: 'translate(-30%, 30%)' }} />
        </div>
        <div className="max-w-5xl mx-auto px-4 py-14 sm:py-20 relative z-10 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-white/10 backdrop-blur-sm text-white/80 text-xs font-semibold rounded-full mb-6" data-testid="hero-badge">
            <Calendar size={14} /> SFOALC — Événements
          </div>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4" style={{ fontFamily: "'Barlow', sans-serif" }} data-testid="events-title">
            Agenda & Congrès
          </h1>
          <p className="text-white/70 text-base sm:text-lg max-w-2xl mx-auto">
            Congrès, webinaires, formations et ateliers en contactologie médicale
          </p>
        </div>
        <div className="absolute bottom-0 left-0 right-0">
          <svg viewBox="0 0 1000 60" preserveAspectRatio="none" style={{ display: 'block', width: '100%', height: '40px' }}>
            <path fill="#f9fafb" d="M0,60V50C0.9,33,126.7,0,500,0S999.1,33,1000,50V60H0Z" />
          </svg>
        </div>
      </section>

      <div className="max-w-5xl mx-auto px-4 py-10 sm:py-14">

        {/* View mode toggle: À venir / Passés */}
        <div className="flex items-center justify-center gap-1 mb-6 bg-gray-100 rounded-full p-1 w-fit mx-auto" data-testid="view-mode-toggle">
          <button onClick={() => { setViewMode('upcoming'); setFilter('all'); }} className={`px-5 py-2 rounded-full text-sm font-semibold transition-all ${viewMode === 'upcoming' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`} data-testid="toggle-upcoming">
            À venir ({upcoming.length})
          </button>
          <button onClick={() => { setViewMode('past'); setFilter('all'); }} className={`px-5 py-2 rounded-full text-sm font-semibold transition-all ${viewMode === 'past' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`} data-testid="toggle-past">
            Passés ({past.length})
          </button>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-3 mb-10 justify-center" data-testid="stats-bar">
          <button onClick={() => setFilter('all')} className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${filter === 'all' ? 'bg-[#1e3a5f] text-white shadow-md' : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-200'}`} data-testid="filter-all">
            Tous ({activeList.length})
          </button>
          {Object.entries(typeConfig).map(([key, cfg]) => {
            const Icon = cfg.icon;
            const count = activeList.filter(e => e.event_type === key).length;
            if (count === 0) return null;
            return (
              <button key={key} onClick={() => setFilter(key)} className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-all ${filter === key ? 'text-white shadow-md' : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-200'}`} style={filter === key ? { background: cfg.color } : {}} data-testid={`filter-${key}`}>
                <Icon size={14} /> {cfg.label} ({count})
              </button>
            );
          })}
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#35AFE4]" /></div>
        ) : filteredList.length === 0 ? (
          <div className="text-center py-20 bg-white rounded-2xl">
            <Calendar size={64} className="mx-auto mb-5 text-gray-300" />
            <h3 className="text-xl font-bold text-gray-700 mb-2" style={{ fontFamily: "'Barlow', sans-serif" }}>
              {viewMode === 'upcoming' ? 'Aucun événement à venir' : 'Aucun événement passé'}
            </h3>
            <p className="text-gray-500 text-sm">
              {viewMode === 'upcoming' ? 'Les prochains événements seront publiés ici.' : 'Revenez plus tard pour consulter les archives.'}
            </p>
          </div>
        ) : (
          <>
            {/* Featured (only for upcoming + all filter) */}
            {viewMode === 'upcoming' && filter === 'all' && featured && (
              <AnimatedCard delay={0}>
                <div className="mb-10">
                  <h2 className="text-xs font-bold text-gray-400 tracking-widest uppercase mb-4" data-testid="upcoming-label">Prochain événement</h2>
                  <EventCard event={featured} featured onNavigate={goToEvent} />
                </div>
              </AnimatedCard>
            )}

            {/* Events list */}
            <div className="space-y-4">
              {(viewMode === 'upcoming' && filter === 'all' ? otherUpcoming : filteredList).map((event, i) => (
                <AnimatedCard key={event.id} delay={100 + i * 80}>
                  <EventCard event={event} onNavigate={goToEvent} />
                </AnimatedCard>
              ))}
            </div>
          </>
        )}

        {/* Newsletter CTA */}
        <AnimatedCard delay={400}>
          <div className="mt-14 bg-white rounded-2xl p-8 sm:p-10 text-center shadow-sm border border-gray-100" data-testid="newsletter-cta">
            <Calendar size={36} className="mx-auto mb-4 text-[#35AFE4]" />
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-3" style={{ fontFamily: "'Barlow', sans-serif" }}>
              Ne manquez aucun événement
            </h2>
            <p className="text-gray-500 text-sm mb-6 max-w-md mx-auto">
              Adhérez à la SFOALC pour recevoir les invitations aux congrès, webinaires et formations
            </p>
            <a href="/adhesion" className="inline-flex items-center gap-2 px-6 py-2.5 bg-[#35AFE4] text-white rounded-full text-sm font-bold hover:bg-[#2d7d9e] transition-all hover:scale-105" data-testid="newsletter-cta-btn">
              Adhérer à la SFOALC <ArrowRight size={14} />
            </a>
          </div>
        </AnimatedCard>
      </div>

      <Footer />
    </div>
  );
};

export default PublicEventsPage;
